import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import './AboutUsPage.css';
import { API_BASE } from './config';
import headOfSchoolPhoto from './assets/dummy.webp';
import aboutHero1 from './assets/news/news1.jpg';
import aboutHero2 from './assets/news/news5.jpg';
import aboutHero3 from './assets/news/news8.jpg';

function AboutUsPage({ page }) {
  const [cmsData, setCmsData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch media map (like HomePage does)
  const fetchMediaMap = useCallback(async () => {
    try {
      console.log('[AboutUsPage] Fetching media map...');
      // Fetch ALL media (not just images) so we get principal photo too
      const response = await fetch(`${API_BASE}/aboutus/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      if (!response.ok) {
        console.warn('[AboutUsPage] Media fetch failed:', response.status);
        setMediaLoaded(true);
        return;
      }
      
      const result = await response.json();
      console.log('[AboutUsPage] Raw API response:', result);
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        console.log('[AboutUsPage] Processing', result.data.length, 'media items');
        
        result.data.forEach((media, idx) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          const filename = media.filename || media.original_filename || '';
          const fileType = media.file_type || '';
          
          console.log(`[AboutUsPage] Media item ${idx}:`, { id: mediaId, filename, fileType, hasUrl: !!mediaUrl });
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            console.log(`[AboutUsPage] ✓ Media ${mediaId} (${filename}) → ${mediaUrl}`);
          } else {
            console.log(`[AboutUsPage] ✗ Skipped - missing id or url`, { mediaId, mediaUrl });
          }
        });
        
        console.log('[AboutUsPage] ✓ Media map ready:', Object.keys(map).length, 'items');
        console.log('[AboutUsPage] Media IDs:', Object.keys(map).sort().join(', '));
        console.log('[AboutUsPage] Checking for ID 161:', map['161'] ? 'FOUND ✓' : 'NOT FOUND ✗');
        setMediaMap(map);
      } else {
        console.warn('[AboutUsPage] Invalid media response:', result);
      }
    } catch (err) {
      console.error('[AboutUsPage] Media fetch error:', err.message);
    } finally {
      setMediaLoaded(true);
    }
  }, []);

  // Fetch CMS sections
  const fetchCMSSections = useCallback(async () => {
    try {
      console.log('[AboutUsPage] Fetching CMS sections...');
      const response = await fetch(`${API_BASE}/aboutus/cms/sections?published=true&_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        console.log('[AboutUsPage] ✓ Sections loaded:', result.data.length, 'sections');
        
        // Log hero section details
        const heroSec = result.data.find(s => s.section_type === 'about-us-hero-shell');
        if (heroSec) {
          let published = heroSec.content_published;
          if (typeof published === 'string') {
            published = JSON.parse(published);
          }
          console.log('[AboutUsPage] 🔍 Hero section from API:', {
            title: published?.en?.title,
            summary: published?.en?.summary?.substring(0, 100)
          });
        }
        
        setCmsData(result.data);
      }
    } catch (err) {
      console.error('[AboutUsPage] CMS fetch error:', err);
    } finally {
      setDataLoaded(true);
    }
  }, []);

  // Fetch both on mount - PARALLEL
  useEffect(() => {
    console.log('[AboutUsPage] 🚀 Mount - fetching media and CMS data...');
    fetchMediaMap();
    fetchCMSSections();
  }, [fetchMediaMap, fetchCMSSections]);

  // Extract CMS sections  
  const heroSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) {
      console.log('[AboutUsPage] ⚠ heroSection: cmsData is empty');
      return {};
    }
    
    const section = cmsData.find(s => s.section_type === 'about-us-hero-shell');
    if (!section) {
      console.log('[AboutUsPage] ⚠ heroSection: about-us-hero-shell not found. Available:', cmsData.map(s => s.section_type));
      return {};
    }
    
    console.log('[AboutUsPage] 🔍 Found hero section:', { id: section.id, section_type: section.section_type });
    console.log('[AboutUsPage] 🔍 Hero content_published type:', typeof section.content_published);
    console.log('[AboutUsPage] 🔍 Hero content_published raw:', section.content_published);
    
    let published = section.content_published;
    if (typeof published === 'string') {
      try {
        published = JSON.parse(published);
        console.log('[AboutUsPage] ✓ Parsed hero content_published from string');
      } catch (e) {
        console.error('[AboutUsPage] ✗ Failed to parse hero content_published:', e);
        return {};
      }
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✅ Final heroSection.en:', { title: enData.title, summary: enData.summary?.substring(0, 50) + '...' });
    return enData;
  }, [cmsData]);
  
  const cmsPrincipal = cmsData?.find(s => s.section_type === 'about-us-principal-shell')?.content_published?.en || {};
  
  // Extract philosophy section from CMS
  const cmsPhilosophySection = useMemo(() => {
    console.log('[AboutUsPage] Extracting philosophy section from cmsData... (cmsData has', cmsData?.length || 0, 'sections)');
    
    if (!cmsData || cmsData.length === 0) {
      console.log('[AboutUsPage] ⚠ cmsData is empty or null');
      return {};
    }
    
    const section = cmsData.find(s => s.section_type === 'about-us-philosophy-shell');
    if (!section) {
      console.log('[AboutUsPage] ⚠ Philosophy section not found in cmsData');
      console.log('[AboutUsPage] Available sections:', cmsData.map(s => s.section_type));
      return {};
    }
    
    console.log('[AboutUsPage] ✓ Philosophy section found:', { id: section.id, status: section.status });
    
    let published = section.content_published;
    if (!published) {
      console.log('[AboutUsPage] ⚠ content_published is null/undefined');
      return {};
    }
    
    if (typeof published === 'string') {
      published = JSON.parse(published);
      console.log('[AboutUsPage] Parsed JSON from string');
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Extracted en data:', {
      hasItems: Array.isArray(enData.items),
      itemCount: enData.items?.length || 0,
      items: enData.items?.map(i => ({ key: i.key, pointsCount: i.points?.length || 0 }))
    });
    
    return enData;
  }, [cmsData]);
  
  // Get hero slides from CMS - resolve image IDs through mediaMap
  // ONLY compute after BOTH media and CMS data have loaded
  const cmsHeroSlides = (mediaLoaded && dataLoaded) ? (heroSection?.slides || []) : [];
  console.log('[AboutUsPage] mediaLoaded:', mediaLoaded, 'dataLoaded:', dataLoaded);
  console.log('[AboutUsPage] CMS hero slides:', cmsHeroSlides);
  console.log('[AboutUsPage] Current mediaMap:', Object.keys(mediaMap).length, 'media items');
  
  // CRITICAL: Resolve image IDs ONLY - no fallbacks to hardcoded images
  const heroSlides = cmsHeroSlides
    .map(slide => {
      const imageId = slide.image || slide.media_id;
      const resolvedUrl = (imageId && mediaMap[imageId]) ? mediaMap[imageId] : null;
      console.log(`[AboutUsPage] Hero slide: imageId=${imageId} → URL=${resolvedUrl ? resolvedUrl.substring(0, 60) + '...' : 'null'}`);
      return resolvedUrl;
    })
    .filter(url => url !== null && url !== undefined && url !== '');  // Only keep valid URLs
  
  console.log('[AboutUsPage] ✓ Resolved hero slides:', heroSlides.length, 'valid images');
  
  const [activeHero, setActiveHero] = useState(0);
  const [selectedValue, setSelectedValue] = useState(null);
  const [animateWelcome, setAnimateWelcome] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  // Intersection Observer untuk Welcome Message
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimateWelcome(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const welcomeElement = document.getElementById('welcome-message');
    if (welcomeElement) {
      observer.observe(welcomeElement);
    }

    return () => observer.disconnect();
  }, []);

  // Extract history section from CMS - resolve image IDs through mediaMap
  const cmsHistorySection = useMemo(() => {
    console.log('[AboutUsPage] Extracting history section from cmsData... (cmsData has', cmsData?.length || 0, 'sections)');
    
    if (!cmsData || cmsData.length === 0) {
      console.log('[AboutUsPage] ⚠ cmsData is empty or null');
      return {};
    }
    
    const section = cmsData.find(s => s.section_type === 'about-us-history-shell');
    if (!section) {
      console.log('[AboutUsPage] ⚠ History section not found in cmsData');
      console.log('[AboutUsPage] Available sections:', cmsData.map(s => s.section_type));
      return {};
    }
    
    console.log('[AboutUsPage] ✓ History section found:', { id: section.id, status: section.status });
    
    let published = section.content_published;
    if (!published) {
      console.log('[AboutUsPage] ⚠ content_published is null/undefined');
      return {};
    }
    
    if (typeof published === 'string') {
      published = JSON.parse(published);
      console.log('[AboutUsPage] Parsed JSON from string');
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Extracted en data:', {
      hasItems: Array.isArray(enData.items),
      itemCount: enData.items?.length || 0,
      items: enData.items?.map(i => ({ year: i.year, title: i.title }))
    });
    
    return enData;
  }, [cmsData]);

  // Extract Why Started and Established from CMS
  const cmsWhyStarted = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return null;
    const section = cmsData.find(s => s.section_type === 'about-us-why-started-shell');
    if (!section || !section.content_published) return null;
    
    let published = section.content_published;
    if (typeof published === 'string') {
      published = JSON.parse(published);
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Loaded Why Started from CMS:', { headline: enData.headline?.substring(0, 50) });
    return enData;
  }, [cmsData]);

  const cmsEstablished = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return null;
    const section = cmsData.find(s => s.section_type === 'about-us-established-shell');
    if (!section || !section.content_published) return null;
    
    let published = section.content_published;
    if (typeof published === 'string') {
      published = JSON.parse(published);
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Loaded Established from CMS:', { date: enData.date, factCount: enData.facts?.length || 0 });
    return enData;
  }, [cmsData]);

  // Resolve history timeline items with image resolution
  const historyTimeline = useMemo(() => {
    console.log('[AboutUsPage] Computing historyTimeline...');
    console.log('[AboutUsPage] cmsHistorySection keys:', Object.keys(cmsHistorySection || {}));
    console.log('[AboutUsPage] cmsHistorySection.items:', cmsHistorySection?.items);
    
    // If CMS data is available and has items, use it EXCLUSIVELY
    if (cmsHistorySection && Array.isArray(cmsHistorySection.items) && cmsHistorySection.items.length > 0) {
      console.log('[AboutUsPage] ✅ USING CMS HISTORY ITEMS - NOW RESOLVING IMAGES:', {
        itemCount: cmsHistorySection.items.length,
        items: cmsHistorySection.items.map((i, idx) => ({ 
          idx, 
          year: i.year, 
          title: i.title?.substring(0, 50),
          imageId: i.image
        }))
      });
      
      // CRITICAL: Resolve image IDs through mediaMap - SAME PATTERN AS VALUES CARDS & PHILOSOPHY
      const resolvedItems = cmsHistorySection.items.map(item => ({
        ...item,
        image: (item.image && mediaMap[item.image]) ? mediaMap[item.image] : null
      }));
      
      console.log('[AboutUsPage] ✅ Resolved history item images:', {
        itemCount: resolvedItems.length,
        items: resolvedItems.map((i, idx) => ({ 
          idx, 
          year: i.year,
          imageUrl: i.image ? i.image.substring(0, 60) + '...' : 'null'
        }))
      });
      
      return resolvedItems;
    }
    
    // Only use fallback if CMS data is empty or not loaded
    console.log('[AboutUsPage] ⚠ USING FALLBACK HISTORY TIMELINE (CMS data empty or missing)');
    return (page?.history_timeline || [
      {
        year: '2012',
        title: 'Primary Campus Opens',
        description: 'Permata Harapan School opens its first primary campus with a focus on faith, values, and academic excellence.',
        image: null,
      },
      {
        year: '2021',
        title: 'Elementary Programme Launch',
        description: 'The school expands to serve growing families with a strengthened elementary learning pathway.',
        image: null,
      },
      {
        year: '2026',
        title: 'Junior High Programme',
        description: 'SMP opens with enriched academics and leadership formation for early adolescents.',
        image: null,
      },
      {
        year: '2027',
        title: 'Senior High Programme',
        description: 'SMA launches to prepare students for university pathways and global opportunities.',
        image: null,
      },
    ]);
  }, [cmsHistorySection, mediaMap, page?.history_timeline]);

  const [activeHistoryIndex, setActiveHistoryIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHistoryIndex((current) => (current + 1) % historyTimeline.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [historyTimeline.length]);

  const whyStarted = {
    headline: cmsWhyStarted?.headline || page?.why_started?.why_started_headline || 'A promise to families, built for the next generation.',
    paragraph: cmsWhyStarted?.paragraph || page?.why_started?.why_started_paragraph || 'We began with a simple mission: create a safe, joyful place where learning feels meaningful and character grows alongside achievement.',
    tags: cmsWhyStarted?.tags || page?.why_started?.why_started_tags || ['Faith-led foundation', 'Future-ready learning', 'Whole-child care', 'Family partnership'],
    note: cmsWhyStarted?.note || page?.why_started?.why_started_note || 'Growing with our community since 2012.',
  };

  const established = {
    date: cmsEstablished?.date || page?.established?.established_date || 'July 2003',
    facts: cmsEstablished?.facts || page?.established?.established_facts || [
      { label: 'Focus', value: 'Faith-led growth' },
      { label: 'Approach', value: 'Whole-child learning' },
      { label: 'Impact', value: 'Community-first' },
    ],
    cta: cmsEstablished?.cta || page?.established?.established_cta || 'Built on faith, growing with families.',
  };

  const profile = useMemo(() => {
    const resolvePhoto = () => {
      const photoId = cmsPrincipal?.principal_photo_id;
      const photoIdType = typeof photoId;
      const photoIdIsValid = photoId && photoId !== 'null' && photoId !== '';
      
      console.log('[AboutUsPage] 📸 Principal photo resolution:');
      console.log('  photoId from CMS:', photoId, `(type: ${photoIdType})`);
      console.log('  photoId is valid?:', photoIdIsValid);
      console.log('  mediaMap size:', Object.keys(mediaMap).length);
      console.log('  mediaMap keys:', Object.keys(mediaMap).slice(0, 10).join(', '));
      
      if (photoIdIsValid) {
        const photoIdStr = String(photoId);
        const photoIdNum = Number(photoId);
        
        // Try both string and number keys
        let resolvedPhoto = mediaMap[photoIdStr] || mediaMap[photoIdNum] || null;
        
        console.log('  Trying photoId as string:', photoIdStr, '→', resolvedPhoto ? 'FOUND' : 'NOT FOUND');
        console.log('  Trying photoId as number:', photoIdNum, '→', mediaMap[photoIdNum] ? 'FOUND' : 'NOT FOUND');
        console.log('  Final URL:', resolvedPhoto ? resolvedPhoto.substring(0, 80) : 'null (PHOTO NOT FOUND IN MEDIA)');
        
        return resolvedPhoto;
      } else {
        console.log('  ⚠️ photoId is null/empty - no photo uploaded yet');
        return null;
      }
    };

    return {
      name: cmsPrincipal?.name || page?.principal_name || 'Dr. Amelia Hartono',
      credentials: cmsPrincipal?.credentials || page?.principal_credentials || 'M.Ed., Ed.D.',
      role: cmsPrincipal?.role || page?.principal_role || 'Principal / Head of School',
      opening: cmsPrincipal?.opening || page?.principal_opening || 'Dear Parents, Students, and Members of the Permata Harapan Community,',
      paragraphOne:
        cmsPrincipal?.paragraph_one ||
        page?.principal_paragraph_one ||
        'Welcome to Permata Harapan School. Our mission is to nurture young people of character, competence, and compassion. We believe meaningful education combines academic rigor, strong values, and authentic care for every learner.',
      paragraphTwo:
        cmsPrincipal?.paragraph_two ||
        page?.principal_paragraph_two ||
        'Together with families, we build a learning environment where students are encouraged to think critically, lead responsibly, and serve others with integrity. We are committed to preparing each child not only for examinations, but for life and purpose.',
      closing:
        cmsPrincipal?.closing ||
        page?.principal_closing ||
        'Thank you for your trust and partnership. We look forward to walking this journey with you.',
      themeLine:
        cmsPrincipal?.theme_line ||
        page?.principal_theme_line ||
        'Nurturing Character, Inspiring Excellence, Building Futures.',
      photo: resolvePhoto(),
    };
  }, [cmsPrincipal, mediaMap, page]);

  // Extract Leadership from CMS
  const cmsLeadership = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return null;
    const section = cmsData.find(s => s.section_type === 'about-us-leadership-shell');
    if (!section || !section.content_published) return null;
    
    let published = section.content_published;
    if (typeof published === 'string') {
      published = JSON.parse(published);
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Loaded Leadership from CMS:', { itemCount: enData.items?.length || 0 });
    return enData;
  }, [cmsData]);

  const leadershipProfiles = useMemo(() => {
    console.log('[AboutUsPage] Computing leadershipProfiles...');
    console.log('[AboutUsPage] cmsLeadership:', cmsLeadership);
    console.log('[AboutUsPage] cmsLeadership.items:', cmsLeadership?.items);
    
    // If CMS data is available and has items, use it EXCLUSIVELY
    if (cmsLeadership && Array.isArray(cmsLeadership.items) && cmsLeadership.items.length > 0) {
      console.log('[AboutUsPage] ✅ USING CMS LEADERSHIP ITEMS - NOW RESOLVING IMAGES:', {
        itemCount: cmsLeadership.items.length,
        items: cmsLeadership.items.map((i, idx) => ({ 
          idx, 
          name: i.name,
          photoId: i.photo
        }))
      });
      
      // Resolve photo IDs through mediaMap
      const resolvedItems = cmsLeadership.items.map(item => ({
        ...item,
        photo: (item.photo && mediaMap[item.photo]) ? mediaMap[item.photo] : null
      }));
      
      console.log('[AboutUsPage] ✅ Resolved leadership photos:', {
        itemCount: resolvedItems.length,
        items: resolvedItems.map((i, idx) => ({ 
          idx, 
          name: i.name,
          photoUrl: i.photo ? i.photo.substring(0, 60) + '...' : 'null'
        }))
      });
      
      return resolvedItems;
    }
    
    // Only use fallback if CMS data is completely missing or not loaded
    console.log('[AboutUsPage] ⚠ USING FALLBACK LEADERSHIP PROFILES (CMS data empty or missing)');
    console.log('[AboutUsPage] cmsLeadership is', cmsLeadership ? 'present' : 'null');
    return (page?.leadership_profiles || [
      {
        name: 'Dr. Amelia Hartono',
        role: 'Principal / Head of School',
        bio: 'Leads the school vision, learning culture, and long-term academic growth with a focus on faith-centered excellence.',
        photo: null,
      },
      {
        name: 'Mrs. Clara Wijaya',
        role: 'Vice Principal, Academic Affairs',
        bio: 'Oversees curriculum quality, teacher development, and student achievement across every grade level.',
        photo: null,
      },
      {
        name: 'Mr. Daniel Prasetyo',
        role: 'Head of Student Life',
        bio: 'Guides character formation, leadership programs, and student well-being initiatives.',
        photo: null,
      },
      {
        name: 'Mrs. Rina Santoso',
        role: 'Head of Operations',
        bio: 'Ensures daily operations, campus safety, and family services run smoothly for the school community.',
        photo: null,
      },
    ]);
  }, [cmsLeadership, mediaMap, page?.leadership_profiles]);

  const philosophyItems = useMemo(() => {
    console.log('[AboutUsPage] Computing philosophyItems...');
    console.log('[AboutUsPage] cmsPhilosophySection keys:', Object.keys(cmsPhilosophySection || {}));
    console.log('[AboutUsPage] cmsPhilosophySection.items:', cmsPhilosophySection?.items);
    
    // If CMS data is available and has items, use it EXCLUSIVELY
    if (cmsPhilosophySection && Array.isArray(cmsPhilosophySection.items) && cmsPhilosophySection.items.length > 0) {
      console.log('[AboutUsPage] ✅ USING CMS PHILOSOPHY ITEMS - NOW RESOLVING IMAGES:', {
        itemCount: cmsPhilosophySection.items.length,
        items: cmsPhilosophySection.items.map((i, idx) => ({ 
          idx, 
          key: i.key, 
          title: i.title?.substring(0, 50),
          pointsCount: i.points?.length || 0,
          imageId: i.image
        }))
      });
      
      // CRITICAL: Resolve image IDs through mediaMap - SAME PATTERN AS VALUES CARDS
      const resolvedItems = cmsPhilosophySection.items.map(item => ({
        ...item,
        image: (item.image && mediaMap[item.image]) ? mediaMap[item.image] : null
      }));
      
      console.log('[AboutUsPage] ✅ Resolved philosophy item images:', {
        itemCount: resolvedItems.length,
        items: resolvedItems.map((i, idx) => ({ 
          idx, 
          key: i.key, 
          imageUrl: i.image ? i.image.substring(0, 60) + '...' : 'null'
        }))
      });
      
      return resolvedItems;
    }
    
    // Only use fallback if CMS data is empty or not loaded
    console.log('[AboutUsPage] ⚠ USING FALLBACK PHILOSOPHY ITEMS (CMS data empty or missing)');
    return (page?.school_philosophy || [
      {
        key: 'motto',
        label: 'School Motto',
        shortTitle: 'Non Scholae Sed Vitae Discimus',
        title: 'Non Scholae Sed Vitae Discimus',
        description:
          'The true purpose of education extends beyond academic achievement or the completion of schooling it is to cultivate the knowledge, skills, and character essential for meaningful success in life.',
        quote: 'Non Scholae Sed Vitae Discimus',
        index: '01',
        image: null,
        points: []
      },
      {
        key: 'philosophy',
        label: 'Philosophy',
        shortTitle: 'Faith, Fortitude, Fulfilment',
        title: 'Faith, Fortitude, and Fulfilment',
        description: '',
        quote:
          'Together, Faith, Fortitude, and Fulfilment reflect the holistic qualities that Permata Harapan Secondary seeks to develop in every student. These core values are expressed through a set of guiding dispositions that shape students\' character, mindset, and actions.',
        index: '02',
        image: null,
        points: [
          {
            title: 'Faith',
            description:
              'Faith serves as the foundational pillar of student character and development. It cultivates core values that shape how students think, act, and relate to others — encompassing spiritual grounding, moral and ethical guidance, and a genuine commitment to respect and tolerance.',
          },
          {
            title: 'Fortitude',
            description:
              'Fortitude embodies the strength, courage, and perseverance required to face and overcome adversity. It enables students to remain steadfast in the face of challenges, stay committed to their goals, and grow through difficulties with resilience and determination.',
          },
          {
            title: 'Fulfilment',
            description:
              'Fulfilment represents the sense of purpose and accomplishment that comes from personal growth and meaningful service to others. It is realised when students develop their full potential while contributing positively to the communities they are part of.',
          },
        ],
      },
      {
        key: 'logo',
        label: 'Logo Philosophy',
        shortTitle: 'Five Elements of Excellence',
        title: 'Logo Philosophy',
        description: '',
        quote: null,
        index: '03',
        image: null,
        points: [
          {
            title: 'Five-Leaved Tree',
            description:
              'A symbol of growth at Permata Harapan Secondary School. Each leaf reflects the school\'s commitment to knowledge, character, leadership, creativity, and global awareness.',
          },
          {
            title: 'Two Students in Graduation Gowns',
            description:
              'Represent Permata Harapan students on their journey to success. Standing side by side, they show unity, mutual support, and shared achievement within the school community.',
          },
          {
            title: 'Star',
            description:
              'A symbol that inspires Permata Harapan students to dream big and aim high. It represents excellence, hope, and future aspirations nurtured by the school.',
          },
          {
            title: 'Circle',
            description:
              'Represents balance, harmony, and unity in the Permata Harapan learning environment, inspired by the concept of Yin and Yang.',
          },
          {
            title: 'Ribbon',
            description:
              'A symbol of honor and celebration, reflecting Permata Harapan Secondary School\'s pride in every student\'s effort and achievement.',
          },
        ],
      },
    ]);
  }, [cmsPhilosophySection, mediaMap, page?.school_philosophy]);
  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    if (!isAutoPlay) return;

    const intervalId = setInterval(() => {
      setActivePhilosophy((current) => (current + 1) % philosophyItems.length);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [isAutoPlay, philosophyItems.length]);

  const goToPreviousPhilosophy = () => {
    setActivePhilosophy((current) =>
      (current - 1 + philosophyItems.length) % philosophyItems.length
    );
    setIsAutoPlay(false);
  };

  const goToNextPhilosophy = () => {
    setActivePhilosophy((current) => (current + 1) % philosophyItems.length);
    setIsAutoPlay(false);
  };

  const handlePhilosophyTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handlePhilosophyTouchEnd = (event) => {
    if (touchStartXRef.current === null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? touchStartXRef.current;
    const deltaX = endX - touchStartXRef.current;
    const threshold = 50;
    if (Math.abs(deltaX) >= threshold) {
      if (deltaX > 0) {
        goToPreviousPhilosophy();
      } else {
        goToNextPhilosophy();
      }
    }
    touchStartXRef.current = null;
  };

  // Load Vision Mission Values from CMS
  const cmsPrincipalData = useMemo(() => {
    const section = cmsData?.find(s => s.section_type === 'about-us-values-shell');
    if (!section) {
      console.log('[AboutUsPage] Values section not found in cmsData');
      return {};
    }
    
    let published = section.content_published;
    if (typeof published === 'string') {
      published = JSON.parse(published);
    }
    
    const enData = published?.en || {};
    console.log('[AboutUsPage] ✓ Loaded values section:', {
      hasCards: Array.isArray(enData.cards),
      cardCount: enData.cards?.length || 0,
      kicker: enData.kicker,
      title: enData.title
    });
    
    return enData;
  }, [cmsData]);
  
  const valuesSection = useMemo(() => {
    if (!cmsPrincipalData || Object.keys(cmsPrincipalData).length === 0) {
      console.log('[AboutUsPage] Using fallback values (no CMS data)');
      // Fallback to default
      return {
        kicker: page?.values_kicker || 'Our Direction',
        title: page?.values_title || 'Vision, Mission, and Core Values',
        intro: page?.values_intro || 'These guiding principles shape how we teach, lead, and care for every student at Permata Harapan School.',
        cards: [
          {
            key: 'vision',
            label: page?.vision_label || 'Vision',
            title: page?.vision_title || 'To be inspired by faith, to be strengthened with fortitude, and to be motivated to achieve fulfillment through lifelong learning',
            description: page?.vision_description || 'Our vision guides every decision we make, ensuring that faith, courage, and purpose are at the heart of our educational journey.',
            image: null,
          },
          {
            key: 'mission',
            label: page?.mission_label || 'Mission',
            title: page?.mission_title || 'PHB Secondary School is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
            description: page?.mission_description || 'We empower every student to become confident learners, compassionate leaders, and responsible global citizens through rigorous academics and character formation.',
            image: null,
          },
          {
            key: 'core-values',
            label: page?.core_values_label || 'Core Values',
            title: page?.core_values_title || 'Sekolah Permata Harapan Secondary is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
            description: page?.core_values_description || 'Our core values of integrity, excellence, compassion, and service define who we are and guide how we treat one another every day.',
            image: null,
          },
        ]
      };
    }

    // CMS data available - resolve image IDs through mediaMap
    const resolvedCards = (cmsPrincipalData.cards || []).map(card => ({
      ...card,
      image: (card.image && mediaMap[card.image]) ? mediaMap[card.image] : null
    }));

    console.log('[AboutUsPage] Using CMS values with', resolvedCards.length, 'cards');
    
    return {
      kicker: cmsPrincipalData.kicker || page?.values_kicker || 'Our Direction',
      title: cmsPrincipalData.title || page?.values_title || 'Vision, Mission, and Core Values',
      intro: cmsPrincipalData.intro || page?.values_intro || 'These guiding principles shape how we teach, lead, and care for every student at Permata Harapan School.',
      cards: resolvedCards
    };
  }, [cmsPrincipalData, mediaMap, page]);

  return (
    <section className="about-us-page">
      {heroSlides.length > 0 && (
      <section className="about-hero" id="about-overview">
        <div className="about-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`about-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="About us hero" />
            </div>
          ))}
        </div>
        <div className="about-hero-overlay" aria-hidden="true" />
        <div className="about-hero-content">
          <p className="about-hero-kicker">About Us</p>
          {(() => {
            const titleToShow = heroSection?.title || page?.hero_title || page?.title || 'About Us';
            const summaryToShow = heroSection?.summary || page?.hero_summary ||
              'Discover our story, values, and the people who shape Permata Harapan School.';
            console.log('[AboutUsPage RENDER] Hero display:', {
              heroSection_title: heroSection?.title,
              page_hero_title: page?.hero_title,
              page_title: page?.title,
              final_title: titleToShow,
              heroSection_summary: heroSection?.summary?.substring(0, 50),
              final_summary: summaryToShow?.substring(0, 50)
            });
            return null;
          })()}
          <h2>{heroSection?.title || page?.hero_title || page?.title || 'About Us'}</h2>
          <p>
            {heroSection?.summary || page?.hero_summary ||
              'Discover our story, values, and the people who shape Permata Harapan School.'}
          </p>
          <div className="about-hero-dots" role="tablist" aria-label="About hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={index}
                type="button"
                className={`about-hero-dot ${index === activeHero ? 'active' : ''}`}
                onClick={() => setActiveHero(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      <div className={`about-us-layout about-animate ${animateWelcome ? 'animate-in' : ''}`} id="welcome-message">
        <aside className="about-photo-pane" aria-label="Head of School">
          <div className="about-photo-blob" aria-hidden="true" />
          <div className="about-photo-card about-animate-up">
            {profile.photo ? (
              <div className="about-head-photo-frame">
                <img src={profile.photo} alt="Head of School" className="about-head-photo" />
              </div>
            ) : (
              <div className="about-head-photo-frame" style={{ backgroundColor: '#f0f0f0', backgroundImage: 'linear-gradient(135deg, #f5f5f5 0%, #efefef 100%)' }}>
                {/* Empty placeholder - no photo uploaded yet */}
              </div>
            )}
          </div>
          <div className="about-photo-caption">
            <p className="about-side-role">{profile.role}</p>
            <p className="about-side-quote">{profile.name}</p>
            <p className="about-side-theme">{profile.themeLine}</p>
          </div>
        </aside>

        <article className="about-message-pane about-animate-up">
          <div className="about-message-inner">
            <p className="about-message-kicker">{page?.welcome_kicker || 'Welcome Message'}</p>
            <h2 className="about-message-title">{page?.welcome_title || 'From the Principal / Head of School'}</h2>
            <p className="about-message-subtitle">{page?.welcome_subtitle || 'A message of vision, values, and partnership'}</p>
            <p className="about-message-intro">{profile.opening}</p>
            <p>{profile.paragraphOne}</p>
            <p>{profile.paragraphTwo}</p>
            <p>{profile.closing}</p>
            <div className="about-message-sign">
              <strong>{profile.name}, {profile.credentials}</strong>
              <span>{profile.role} • Permata Harapan School</span>
            </div>
          </div>
        </article>
      </div>

      <div className="about-us-values-shell" id="vision-mission-values">
        <div className="about-us-values-header">
          <p className="about-values-kicker">{valuesSection.kicker}</p>
          <h3>{valuesSection.title}</h3>
          <p>{valuesSection.intro}</p>
        </div>

        <div className="about-values-grid">
          {valuesSection.cards.map((card) => (
            <div
              className={`value-card-new ${selectedValue?.key === card.key ? 'expanded' : ''}`}
              key={card.key}
              onClick={() => setSelectedValue(selectedValue?.key === card.key ? null : card)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedValue(selectedValue?.key === card.key ? null : card);
                }
              }}
            >
              {selectedValue?.key !== card.key && (
                <>
                  <div className="value-card-new-image">
                    <img src={card.image} alt={card.label} />
                    <div className="value-card-new-image-overlay">
                      <h4>{card.label}</h4>
                    </div>
                  </div>
                  <div className="value-card-new-content">
                    <h3>{card.label}</h3>
                  </div>
                </>
              )}

              {selectedValue?.key === card.key && (
                <div className="value-card-expanded-content">
                  <h4>{card.title}</h4>
                  <p>{card.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="about-philosophy-shell" id="school-philosophy">
        <div className="about-philosophy-header">
          <p className="about-values-kicker">School Philosophy</p>
          <h3>Our Guiding Principles</h3>
          <p>
            Discover the core values and vision that define Permata Harapan School
          </p>
        </div>
        <div className="about-philosophy-modern">
          <div className="about-philosophy-tabs" role="tablist" aria-label="School philosophy">
            {philosophyItems.map((item, index) => (
              <button
                key={item.key}
                id={`philosophy-tab-${item.key}`}
                type="button"
                className={`about-philosophy-tab ${index === activePhilosophy ? 'active' : ''}`}
                onClick={() => {
                  setActivePhilosophy(index);
                  setIsAutoPlay(false);
                }}
                role="tab"
                aria-selected={index === activePhilosophy}
                aria-controls="philosophy-panel"
              >
                <span className="about-philosophy-tab-index">{item.index}</span>
                <span className="about-philosophy-tab-label">{item.label}</span>
                <span className="about-philosophy-tab-title">{item.shortTitle}</span>
              </button>
            ))}
          </div>

          <div className="about-philosophy-panel-wrapper">
            <button
              type="button"
              className="about-philosophy-nav about-philosophy-nav-prev"
              onClick={goToPreviousPhilosophy}
              aria-label="Show previous philosophy"
            >
              ‹
            </button>
            <article
              className="about-philosophy-panel"
              id="philosophy-panel"
              role="tabpanel"
              aria-labelledby={`philosophy-tab-${philosophyItems[activePhilosophy].key}`}
              key={philosophyItems[activePhilosophy].key}
              onTouchStart={handlePhilosophyTouchStart}
              onTouchEnd={handlePhilosophyTouchEnd}
            >
              <div className="about-philosophy-panel-media" aria-hidden="true">
                <img
                  src={philosophyItems[activePhilosophy].image}
                  alt={philosophyItems[activePhilosophy].label}
                />
              </div>
              <div className="about-philosophy-panel-body">
                <div className="about-philosophy-panel-top">
                  <span className="about-philosophy-panel-badge">
                    {philosophyItems[activePhilosophy].label}
                  </span>
                  <span className="about-philosophy-panel-mark">
                    {philosophyItems[activePhilosophy].index}
                  </span>
                </div>
                <h4>{philosophyItems[activePhilosophy].title}</h4>
                {philosophyItems[activePhilosophy].description && (
                  <p className="about-philosophy-panel-description">
                    {philosophyItems[activePhilosophy].description}
                  </p>
                )}
                {philosophyItems[activePhilosophy].points?.length > 0 && (
                  <div className="about-philosophy-panel-points">
                    {philosophyItems[activePhilosophy].points.map((point, idx) => (
                      <div className="about-philosophy-point" key={point.title} style={{ animationDelay: `${idx * 80}ms` }}>
                        <h5>{point.title}</h5>
                        <p>{point.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {philosophyItems[activePhilosophy].quote && (
                  <p className="about-philosophy-panel-quote">
                    "{philosophyItems[activePhilosophy].quote}"
                  </p>
                )}
              </div>
            </article>
            <button
              type="button"
              className="about-philosophy-nav about-philosophy-nav-next"
              onClick={goToNextPhilosophy}
              aria-label="Show next philosophy"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="about-history-shell" id="our-history">
        <div className="about-history-header">
          <p className="about-values-kicker">History</p>
          <h3>Our Beginning</h3>
          <p>
            A brief story of how and why Permata Harapan School was founded.
          </p>
        </div>
        <div className="history-modern">
          <div
            className="history-modern-stage"
            id="history-panel"
            role="tabpanel"
            style={{ backgroundImage: `url(${historyTimeline[activeHistoryIndex]?.image})` }}
          >
            <div className="history-modern-overlay" aria-hidden="true" />
            <div className="history-modern-content">
              <div className="history-modern-top">
                <span className="history-modern-index">0{activeHistoryIndex + 1}</span>
                <span className="history-modern-year">{historyTimeline[activeHistoryIndex]?.year}</span>
              </div>
              <h4>{historyTimeline[activeHistoryIndex]?.title}</h4>
              <p>{historyTimeline[activeHistoryIndex]?.description}</p>
            </div>
          </div>

          <div className="history-modern-list" role="tablist" aria-label="History timeline">
            {historyTimeline.map((item, index) => (
              <button
                key={item.year}
                type="button"
                className={`history-modern-item ${index === activeHistoryIndex ? 'active' : ''}`}
                onClick={() => setActiveHistoryIndex(index)}
                role="tab"
                aria-selected={index === activeHistoryIndex}
                aria-controls="history-panel"
              >
                <span className="history-modern-item-year">{item.year}</span>
                <span className="history-modern-item-title">{item.title}</span>
              </button>
            ))}
          </div>
        </div>

      <div className="history-modern-footer">
          <article className="history-modern-story">
            <p className="history-story-kicker">Why We Started</p>
            <h4>{whyStarted.headline}</h4>
            <p className="history-modern-story-text">{whyStarted.paragraph}</p>
            <div className="history-modern-story-highlights">
              {whyStarted.tags.map((tag, i) => (
                <span key={i}>{tag}</span>
              ))}
            </div>
            <p className="history-modern-story-note">{whyStarted.note}</p>
          </article>

          <div className="history-modern-established">
            <div className="history-established-top">
              <div>
                <p className="history-established-kicker">Established</p>
                <h4>{established.date}</h4>
              </div>
              <span className="history-established-badge">Since</span>
            </div>
            <div className="history-established-facts">
              {established.facts.map((fact, i) => (
                <div key={i} className="history-established-fact">
                  <span className="history-fact-label">{fact.label}</span>
                  <strong>{fact.value}</strong>
                </div>
              ))}
            </div>
            <div className="history-established-cta">
              <span>{established.cta}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="about-leadership-shell" id="school-leadership">
        <div className="about-leadership-header">
          <p className="about-leadership-kicker">School Leadership</p>
          <h3>Profiles of the principal and key staff</h3>
          <p>
            Meet the leaders who shape our culture, strengthen academics, and support every student journey.
          </p>
        </div>

        <div className="about-leadership-grid">
          {leadershipProfiles.map((leader) => (
            <article className="leadership-card" key={leader.name}>
              <div className="leadership-photo">
                <img src={leader.photo} alt={leader.name} />
              </div>
              <div className="leadership-body">
                <p className="leadership-role">{leader.role}</p>
                <h4>{leader.name}</h4>
                <p>{leader.bio}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default AboutUsPage;
